import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Eyebrow } from '@/components/ui/Typography';
import { Button } from '@/components/ui/Button';

interface Props {
  ssid: string;
  password: string;
  onClose: () => void;
}

type Phase = 'idle' | 'connecting' | 'connected' | 'error';
type CopiedField = 'ssid' | 'password' | null;

// Mesmo padrão de `useUserPosition.ts` (geolocationModule) — nunca resolver o
// proxy do plugin através de `await` num import estático no topo do módulo.
async function wifiModule() {
  return import('@capgo/capacitor-wifi');
}

function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Modal de Wi-Fi da estação. Android nativo entra na rede com 1 toque
 * (`@capgo/capacitor-wifi`). iOS aguarda o entitlement "Hotspot Configuration"
 * da Apple (protocolado, sem ETA) — até lá, e no web, mostra SSID/senha pra
 * copiar (feedback Saulo 2026-07-06: QR na própria tela que precisa entrar na
 * rede não funciona — não dá pra escanear a própria tela do aparelho).
 */
export function WifiJoinModal({ ssid, password, onClose }: Props) {
  const android = isAndroidNative();
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<CopiedField>(null);

  async function handleConnect() {
    setPhase('connecting');
    setErrorMsg(null);
    try {
      const { CapacitorWifi } = await wifiModule();
      const perm = await CapacitorWifi.checkPermissions();
      if (perm.location !== 'granted') {
        const req = await CapacitorWifi.requestPermissions({ permissions: ['location'] });
        if (req.location !== 'granted') {
          setPhase('error');
          setErrorMsg('Precisa da permissão de localização pro Android entrar no Wi-Fi.');
          return;
        }
      }
      await CapacitorWifi.connect({ ssid, password });
      setPhase('connected');
    } catch {
      setPhase('error');
      setErrorMsg('Não deu pra conectar automaticamente. Tente manualmente abaixo.');
    }
  }

  async function copy(value: string, field: Exclude<CopiedField, null>) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(field);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard indisponível — o valor já aparece como texto pra copiar à mão.
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-bow-royal/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex w-full max-w-md flex-col items-center gap-4 rounded-t-3xl bg-white p-6 shadow-deep sm:mb-8 sm:rounded-3xl"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <Eyebrow>Wi-Fi da estação</Eyebrow>

        {phase === 'connected' ? (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-bow-green/10 text-bow-green">
              <CheckGlyph size={30} />
            </div>
            <p className="text-center text-sm font-semibold text-bow-royal">
              Conectado à rede {ssid}
            </p>
          </>
        ) : android ? (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-bow-blue/10 text-bow-blue">
              <WifiGlyph size={30} />
            </div>
            <div className="text-center">
              <div className="text-base font-semibold text-bow-royal">{ssid}</div>
              <p className="mt-1 text-sm text-bow-royal/60">
                {phase === 'connecting' ? 'Conectando…' : 'Toque pra entrar na rede automaticamente.'}
              </p>
            </div>
            {phase === 'error' && errorMsg && (
              <p className="text-center text-sm text-bow-red">{errorMsg}</p>
            )}
            <Button
              variant="primary"
              size="md"
              onClick={handleConnect}
              disabled={phase === 'connecting'}
              fullWidth
            >
              {phase === 'connecting' ? 'Conectando…' : 'Conectar'}
            </Button>
            {phase === 'error' && (
              <ManualFallback ssid={ssid} password={password} copied={copied} onCopy={copy} />
            )}
          </>
        ) : (
          <ManualFallback ssid={ssid} password={password} copied={copied} onCopy={copy} />
        )}

        <Button variant="ghost" size="md" onClick={onClose} fullWidth>
          Fechar
        </Button>
      </div>
    </div>
  );
}

function ManualFallback({
  ssid,
  password,
  copied,
  onCopy
}: {
  ssid: string;
  password: string;
  copied: CopiedField;
  onCopy: (value: string, field: Exclude<CopiedField, null>) => void;
}) {
  return (
    <div className="flex w-full flex-col gap-3">
      <p className="text-center text-sm text-bow-royal/70">
        Abra as configurações de Wi-Fi do celular e conecte manualmente com os dados abaixo.
      </p>
      <CopyRow label="Rede" value={ssid} copied={copied === 'ssid'} onCopy={() => onCopy(ssid, 'ssid')} />
      <CopyRow
        label="Senha"
        value={password}
        copied={copied === 'password'}
        onCopy={() => onCopy(password, 'password')}
      />
    </div>
  );
}

function CopyRow({
  label,
  value,
  copied,
  onCopy
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-bow-cream-2/50 px-4 py-3">
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-bow-royal/50">
          {label}
        </div>
        <div className="truncate text-sm font-semibold text-bow-royal">{value}</div>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="flex-shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-bow-royal shadow-sm"
      >
        {copied ? 'Copiado!' : 'Copiar'}
      </button>
    </div>
  );
}

function WifiGlyph({ size = 22 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4.5 9.5a11 11 0 0 1 15 0" />
      <path d="M7.5 13a7 7 0 0 1 9 0" />
      <path d="M10.5 16.5a3 3 0 0 1 3 0" />
      <circle cx="12" cy="19.5" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CheckGlyph({ size = 22 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}
